import { useCallback, useEffect, useMemo, useState } from 'react';
import { TaskFormModal } from '../tasks/TaskFormModal';
import { VisitationAddAccountsModal } from './VisitationAddAccountsModal';
import { Modal } from '../ui/Modal';
import { useCrmData } from '../../contexts/CrmDataContext';
import { usePortal } from '../../contexts/PortalContext';
import { useMonthlyVisitationRealtime } from '../../hooks/useMonthlyVisitationRealtime';
import {
  addMonthlyVisitationItems,
  addMonthlyVisitationProspect,
  fetchMonthlyVisitationItems,
  linkMonthlyVisitationTask,
  removeMonthlyVisitationItem,
  setMonthlyVisitationVisited,
  visitationItemDisplayName,
  visitationMonthOptions,
  type MonthlyVisitationItem,
} from '../../lib/monthlyVisitation';
import type { Task } from '../../types/crm';
import { clientsForAccountPicker, idsMatch } from '../../utils/crmHelpers';
import { ownerIdForUser } from '../../utils/ownerId';
import { currentMonthKeySingapore } from '../../utils/taskHelpers';

const INITIAL_VISIBLE = 8;

export function DashboardMonthlyVisitation() {
  const {
    data,
    getMyData,
    username,
    ownerIdKind,
    refresh,
    effectiveRole,
    supervisorTeam,
    actorSalespersonCodes,
    departmentSlug,
    userFilter,
    canUseUserFilter,
    ownerOptions,
    ownerLabel,
  } = useCrmData();
  const { kind: portalKind } = usePortal();
  const monthOptions = useMemo(() => visitationMonthOptions(), []);
  const [monthKey, setMonthKey] = useState(() => currentMonthKeySingapore());
  const [items, setItems] = useState<MonthlyVisitationItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [taskForItem, setTaskForItem] = useState<MonthlyVisitationItem | null>(null);
  const [allOpen, setAllOpen] = useState(false);

  const me = useMemo(
    () => data.users.find((u) => u.username === username) ?? null,
    [data.users, username],
  );
  const myOwnerId = me ? ownerIdForUser(me, ownerIdKind) : username;

  /** Align with topbar User filter so elevated users can view others' visitation lists. */
  const listOwnerId = useMemo(() => {
    if (!canUseUserFilter || userFilter === 'All' || userFilter === 'Me') {
      return myOwnerId;
    }
    const filterUser = data.users.find(
      (u) => u.username.trim().toLowerCase() === String(userFilter).trim().toLowerCase(),
    );
    if (filterUser) return ownerIdForUser(filterUser, ownerIdKind);
    const fromOptions = ownerOptions.find(
      (o) => o.username.trim().toLowerCase() === String(userFilter).trim().toLowerCase(),
    );
    return fromOptions?.value ?? myOwnerId;
  }, [canUseUserFilter, userFilter, myOwnerId, data.users, ownerIdKind, ownerOptions]);

  const viewingOther =
    canUseUserFilter &&
    userFilter !== 'All' &&
    userFilter !== 'Me' &&
    String(userFilter).trim().toLowerCase() !== username.trim().toLowerCase();

  const ownerId = listOwnerId;

  const scopedClients = useMemo(
    () =>
      clientsForAccountPicker(
        getMyData('clients'),
        effectiveRole,
        username,
        data.users,
        supervisorTeam,
        actorSalespersonCodes,
        departmentSlug,
      ),
    [getMyData, effectiveRole, username, data.users, supervisorTeam, actorSalespersonCodes, departmentSlug],
  );
  const displayClients = getMyData('clients');
  const tasks = getMyData('tasks');

  const clientByIdMap = useMemo(
    () => new Map(displayClients.map((c) => [String(c.id), c])),
    [displayClients],
  );

  const listedIds = useMemo(
    () => new Set(items.map((i) => i.client_id).filter((id): id is string => Boolean(id))),
    [items],
  );
  const pickableClients = useMemo(
    () => scopedClients.filter((c) => !listedIds.has(String(c.id))),
    [scopedClients, listedIds],
  );

  /** Unvisited on top; visited sink to bottom (keep sort_order within each group). */
  const sortedItems = useMemo(
    () =>
      [...items].sort((a, b) => {
        if (a.visited !== b.visited) return a.visited ? 1 : -1;
        if (a.sort_order !== b.sort_order) return a.sort_order - b.sort_order;
        return (a.created_at ?? '').localeCompare(b.created_at ?? '');
      }),
    [items],
  );

  const load = useCallback(async () => {
    if (!ownerId || !monthKey) return;
    setLoading(true);
    setError(null);
    try {
      const rows = await fetchMonthlyVisitationItems(ownerId, monthKey);
      setItems(rows);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load visitation list.');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [ownerId, monthKey]);

  useEffect(() => {
    void load();
  }, [load]);

  useMonthlyVisitationRealtime({
    enabled: Boolean(ownerId && monthKey),
    portalKind: portalKind === 'it' ? 'it' : 'crm',
    ownerId,
    onChange: () => void load(),
  });

  useEffect(() => {
    const refreshVisitation = () => void load();
    window.addEventListener('bth:visitation-refresh', refreshVisitation);
    return () => window.removeEventListener('bth:visitation-refresh', refreshVisitation);
  }, [load]);

  useEffect(() => {
    setAllOpen(false);
  }, [monthKey]);

  function hasTaskForItem(item: MonthlyVisitationItem): boolean {
    if (item.task_id) return true;
    if (!item.client_id) return false;
    return tasks.some((t) => t.client_id != null && idsMatch(t.client_id, item.client_id));
  }

  async function handleAddAccounts(clientIds: string[]) {
    if (!ownerId || clientIds.length === 0) return;
    setAdding(true);
    setError(null);
    try {
      const result = await addMonthlyVisitationItems({
        ownerId,
        monthKey,
        clientIds,
        startSortOrder: items.length,
      });
      setPickerOpen(false);
      await load();
      if (result.added === 0 && result.skipped > 0) {
        setError('Those accounts are already on this month’s list.');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to add accounts.');
    } finally {
      setAdding(false);
    }
  }

  async function handleAddProspect(companyName: string) {
    if (!ownerId) return;
    setAdding(true);
    setError(null);
    try {
      await addMonthlyVisitationProspect({
        ownerId,
        monthKey,
        companyName,
        sortOrder: items.length,
      });
      setPickerOpen(false);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to add new customer.');
      throw e;
    } finally {
      setAdding(false);
    }
  }

  async function handleRemove(item: MonthlyVisitationItem) {
    setBusyId(item.id);
    setError(null);
    try {
      await removeMonthlyVisitationItem(item.id);
      setItems((prev) => prev.filter((row) => row.id !== item.id));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to remove.');
    } finally {
      setBusyId(null);
    }
  }

  async function handleToggleVisited(item: MonthlyVisitationItem) {
    if (!hasTaskForItem(item)) return;
    setBusyId(item.id);
    setError(null);
    try {
      const updated = await setMonthlyVisitationVisited(item.id, !item.visited);
      setItems((prev) => prev.map((row) => (row.id === item.id ? updated : row)));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to update visited.');
    } finally {
      setBusyId(null);
    }
  }

  async function handleTaskSaved(task: Task) {
    if (!taskForItem) return;
    const itemId = taskForItem.id;
    setTaskForItem(null);
    try {
      if (task.id) {
        const updated = await linkMonthlyVisitationTask(
          itemId,
          String(task.id),
          task.client_id ? String(task.client_id) : null,
        );
        setItems((prev) => prev.map((row) => (row.id === itemId ? updated : row)));
      }
      await refresh();
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Task saved but could not link to list.');
      await refresh();
    }
  }

  const visitedCount = sortedItems.filter((i) => i.visited).length;
  const visibleItems = sortedItems.slice(0, INITIAL_VISIBLE);
  const hasHidden = sortedItems.length > INITIAL_VISIBLE;
  const prospectTask = Boolean(taskForItem && !taskForItem.client_id);
  const monthLabel =
    monthOptions.find((option) => option.value === monthKey)?.label ?? monthKey;

  function renderVisitationRow(item: MonthlyVisitationItem) {
    const client = item.client_id ? clientByIdMap.get(String(item.client_id)) : undefined;
    const displayName = visitationItemDisplayName(item, client?.company_name);
    const isProspect = !item.client_id;
    const canVisit = hasTaskForItem(item);
    const busy = busyId === item.id;
    return (
      <li
        key={item.id}
        className={`dashboard-visitation-row${item.visited ? ' dashboard-visitation-row--visited' : ''}`}
      >
        <div className="dashboard-visitation-row__main">
          <span className="dashboard-visitation-row__name">{displayName}</span>
          {isProspect ? (
            <span className="dashboard-visitation-row__badge">New</span>
          ) : client?.code ? (
            <span className="dashboard-visitation-row__code">{client.code}</span>
          ) : null}
        </div>
        <div className="dashboard-visitation-row__actions">
          <button
            type="button"
            className="btn-secondary btn-sm"
            disabled={busy}
            onClick={() => setTaskForItem(item)}
          >
            Add task
          </button>
          <button
            type="button"
            className={`btn-sm${item.visited ? ' btn-primary' : ' btn-secondary'}`}
            disabled={busy || !canVisit}
            title={
              canVisit
                ? item.visited
                  ? 'Mark as not visited'
                  : 'Mark as visited'
                : 'Add a task first'
            }
            onClick={() => void handleToggleVisited(item)}
          >
            {item.visited ? 'Visited' : 'Visited?'}
          </button>
          <button
            type="button"
            className="btn-secondary btn-sm dashboard-visitation-row__remove"
            disabled={busy}
            aria-label="Remove"
            title="Remove from list"
            onClick={() => void handleRemove(item)}
          >
            ×
          </button>
        </div>
      </li>
    );
  }

  return (
    <>
      <section className="card dashboard-panel dashboard-panel--visitation">
        <div className="dashboard-panel__head dashboard-visitation__head">
          <h2 className="detail-section-title">Monthly Visitation List</h2>
          <div className="dashboard-visitation__head-actions">
            <label className="dashboard-period">
              <span className="dashboard-period__label">Month</span>
              <select
                className="field-input field-input--compact"
                value={monthKey}
                onChange={(e) => setMonthKey(e.target.value)}
              >
                {monthOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="button"
              className="btn-primary btn-sm"
              aria-label="Add to visitation list"
              title="Add accounts or new customer"
              onClick={() => setPickerOpen(true)}
            >
              +
            </button>
          </div>
        </div>

        {error ? <div className="alert alert-error">{error}</div> : null}

        {loading ? (
          <p className="detail-text dashboard-panel__empty">Loading…</p>
        ) : items.length === 0 ? (
          <p className="detail-text dashboard-panel__empty">
            No visits planned for this month. Use + to add existing accounts or a new customer.
          </p>
        ) : (
          <>
            <ul className="dashboard-visitation-list">
              {visibleItems.map((item) => renderVisitationRow(item))}
            </ul>
            {hasHidden ? (
              <div className="dashboard-visitation__more">
                <button
                  type="button"
                  className="btn-secondary btn-sm"
                  onClick={() => setAllOpen(true)}
                >
                  Show all ({sortedItems.length})
                </button>
              </div>
            ) : null}
          </>
        )}
      </section>

      <Modal
        title={`Monthly Visitation · ${monthLabel}`}
        open={allOpen}
        onClose={() => setAllOpen(false)}
        wide
        panelClassName="dashboard-visitation-all-modal"
      >
        <p className="dashboard-panel__hint dashboard-visitation-all-modal__hint">
          {visitedCount}/{sortedItems.length} visited
          {viewingOther ? ` · ${ownerLabel(listOwnerId)}` : ''}
        </p>
        <ul className="dashboard-visitation-list dashboard-visitation-list--all">
          {sortedItems.map((item) => renderVisitationRow(item))}
        </ul>
      </Modal>

      <VisitationAddAccountsModal
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        clients={pickableClients}
        adding={adding}
        onAdd={handleAddAccounts}
        onAddProspect={handleAddProspect}
      />

      <TaskFormModal
        open={Boolean(taskForItem)}
        onClose={() => setTaskForItem(null)}
        defaultClientId={taskForItem?.client_id ? String(taskForItem.client_id) : undefined}
        lockClient={Boolean(taskForItem?.client_id)}
        defaultProspectName={
          prospectTask && taskForItem ? visitationItemDisplayName(taskForItem) : undefined
        }
        lockProspect={prospectTask}
        onSaved={(task) => void handleTaskSaved(task)}
      />
    </>
  );
}
